// Semantic map — every atom plotted by the UMAP projection of its embedding,
// so distance ≈ meaning. Structure (type/scope/facets) rides on colour + size;
// the kNN edges are nearest neighbours in the FULL 1024-dim space, so the
// constellation lines are true semantic adjacency, not screen proximity.
import { useEffect, useRef } from "react";
import type { AtlasAtom, AtlasCluster, AtlasEdge } from "../../../lib/atlas-types";
import {
  type Camera,
  type Tokens,
  type TooltipPayload,
  newCamera,
  fitCameraToBounds,
  quantileBounds,
  viewScale,
  worldToScreen,
  screenToWorld,
  zoomAt,
  rgba,
  drawHaloText,
  atomRadius,
  ageLabel,
  fitCanvas,
  MONO_10,
} from "./atlas-canvas";

interface Props {
  atoms: AtlasAtom[];
  visible: boolean[];
  knn: AtlasEdge[];
  clusters: AtlasCluster[];
  colorOf: (a: AtlasAtom) => string;
  tokens: Tokens;
  showEdges: boolean;
  showLabels: boolean;
  selectedKey: string | null;
  onSelect: (a: AtlasAtom | null) => void;
  onHover: (h: TooltipPayload | null) => void;
}

const TAU = Math.PI * 2;

export function SemanticMap(props: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const camRef = useRef<Camera>(newCamera());
  const hoverRef = useRef(-1);
  const dragRef = useRef({ on: false, moved: false, x: 0, y: 0 });
  const rafRef = useRef(0);
  const fittedRef = useRef(false);
  const propsRef = useRef(props);
  propsRef.current = props;
  const drawRef = useRef<() => void>(() => {});

  drawRef.current = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const [w, h] = fitCanvas(canvas);
    // Fit-to-data on the first real draw — mount-time rects can race flex layout.
    if (!fittedRef.current && w > 40 && h > 40 && propsRef.current.atoms.length > 0) {
      const b = quantileBounds(propsRef.current.atoms, propsRef.current.visible);
      if (b) {
        camRef.current = fitCameraToBounds(b[0], b[1], b[2], b[3], w, h);
        fittedRef.current = true;
      }
    }
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const { atoms, visible, knn, clusters, colorOf, tokens: T, showEdges, showLabels, selectedKey } = propsRef.current;
    const cam = camRef.current;

    ctx.fillStyle = T.surface;
    ctx.fillRect(0, 0, w, h);

    // Faint world-anchored dot grid — the "instrument bed" that makes pan/zoom legible.
    const g = 1 / 20;
    if (viewScale(cam, w, h) * g >= 13) {
      const [wx0, wy0] = screenToWorld(0, 0, cam, w, h);
      const [wx1, wy1] = screenToWorld(w, h, cam, w, h);
      const i0 = Math.floor(wx0 / g), i1 = Math.ceil(wx1 / g);
      const j0 = Math.floor(wy0 / g), j1 = Math.ceil(wy1 / g);
      if ((i1 - i0) * (j1 - j0) < 6000) {
        ctx.fillStyle = rgba(T.borderStrong, 0.4);
        for (let i = i0; i <= i1; i++) {
          for (let j = j0; j <= j1; j++) {
            const [px, py] = worldToScreen(i * g, j * g, cam, w, h);
            ctx.fillRect(px - 0.5, py - 0.5, 1.2, 1.2);
          }
        }
      }
    }

    // Screen positions once per frame.
    const n = atoms.length;
    const xs = new Float32Array(n);
    const ys = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const p = worldToScreen(atoms[i].x, atoms[i].y, cam, w, h);
      xs[i] = p[0];
      ys[i] = p[1];
    }
    const selIdx = selectedKey ? atoms.findIndex((a) => a.key === selectedKey) : -1;

    // Nearest-neighbour filaments.
    if (showEdges) {
      ctx.lineWidth = 1;
      for (const e of knn) {
        if (!visible[e.a] || !visible[e.b]) continue;
        const x1 = xs[e.a], y1 = ys[e.a], x2 = xs[e.b], y2 = ys[e.b];
        if ((x1 < -40 && x2 < -40) || (x1 > w + 40 && x2 > w + 40) || (y1 < -40 && y2 < -40) || (y1 > h + 40 && y2 > h + 40)) continue;
        ctx.strokeStyle = rgba(T.borderStrong, 0.1 + e.w * 0.28);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.quadraticCurveTo((x1 + x2) / 2 + (y2 - y1) * 0.09, (y1 + y2) / 2 - (x2 - x1) * 0.09, x2, y2);
        ctx.stroke();
      }
    }

    // Atoms. Active = filled dot with a surface ring (the 2px-gap rule);
    // superseded/deprecated = hollow, faded — visibly "not current truth".
    for (let i = 0; i < n; i++) {
      if (!visible[i]) continue;
      const x = xs[i], y = ys[i];
      if (x < -24 || x > w + 24 || y < -24 || y > h + 24) continue;
      const a = atoms[i];
      const r = atomRadius(a.chars, cam.k);
      const color = colorOf(a);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, TAU);
      if (!a.status || a.status === "active" || a.status === "draft") {
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = T.surface;
        ctx.lineWidth = 1.25;
        ctx.stroke();
      } else {
        ctx.fillStyle = rgba(color, 0.13);
        ctx.fill();
        ctx.strokeStyle = rgba(color, 0.55);
        ctx.lineWidth = 1.25;
        ctx.stroke();
      }
    }

    // Selection: ring the atom, surface its true neighbours in colour.
    if (selIdx >= 0 && visible[selIdx]) {
      for (const e of knn) {
        if (e.a !== selIdx && e.b !== selIdx) continue;
        const o = e.a === selIdx ? e.b : e.a;
        if (!visible[o]) continue;
        const oc = colorOf(atoms[o]);
        ctx.strokeStyle = rgba(oc, 0.8);
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(xs[selIdx], ys[selIdx]);
        ctx.lineTo(xs[o], ys[o]);
        ctx.stroke();
        ctx.strokeStyle = oc;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(xs[o], ys[o], atomRadius(atoms[o].chars, cam.k) + 2.5, 0, TAU);
        ctx.stroke();
      }
      const a = atoms[selIdx];
      ctx.strokeStyle = colorOf(a);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(xs[selIdx], ys[selIdx], atomRadius(a.chars, cam.k) + 3.5, 0, TAU);
      ctx.stroke();
    }

    // Hover halo.
    const hi = hoverRef.current;
    if (hi >= 0 && hi < n && visible[hi]) {
      const a = atoms[hi];
      const color = colorOf(a);
      const r = atomRadius(a.chars, cam.k);
      ctx.fillStyle = rgba(color, 0.16);
      ctx.beginPath();
      ctx.arc(xs[hi], ys[hi], r + 7, 0, TAU);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(xs[hi], ys[hi], r + 1, 0, TAU);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = T.surface;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Topic labels at cluster centroids — more reveal as you zoom in.
    if (showLabels) {
      const maxLabels = cam.k < 1.4 ? 9 : cam.k < 3 ? 16 : clusters.length;
      const placed: { x0: number; y0: number; x1: number; y1: number }[] = [];
      ctx.font = MONO_10;
      for (const c of [...clusters].sort((a, b) => b.count - a.count).slice(0, maxLabels)) {
        const [px, py] = worldToScreen(c.x, c.y, cam, w, h);
        if (px < -80 || px > w + 80 || py < -20 || py > h + 20) continue;
        const label = c.label.toUpperCase();
        const tw = ctx.measureText(label).width;
        const rect = { x0: px - tw / 2 - 5, y0: py - 9, x1: px + tw / 2 + 5, y1: py + 9 };
        if (placed.some((q) => !(rect.x1 < q.x0 || rect.x0 > q.x1 || rect.y1 < q.y0 || rect.y0 > q.y1))) continue;
        placed.push(rect);
        drawHaloText(ctx, label, px, py, T.textTertiary, rgba(T.surface, 0.9));
      }
    }
  };

  const schedule = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      drawRef.current();
    });
  };

  // Re-render whenever inputs change.
  useEffect(schedule);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const hitTest = (mx: number, my: number): number => {
      const { atoms, visible } = propsRef.current;
      const rect = canvas.getBoundingClientRect();
      const cam = camRef.current;
      let best = -1;
      let bestD = Infinity;
      for (let i = 0; i < atoms.length; i++) {
        if (!visible[i]) continue;
        const [x, y] = worldToScreen(atoms[i].x, atoms[i].y, cam, rect.width, rect.height);
        const d = Math.hypot(x - mx, y - my);
        const reach = Math.max(9, atomRadius(atoms[i].chars, cam.k) + 3);
        if (d < reach && d < bestD) {
          bestD = d;
          best = i;
        }
      }
      return best;
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      camRef.current = zoomAt(
        camRef.current,
        e.clientX - rect.left,
        e.clientY - rect.top,
        Math.exp(-e.deltaY * 0.0016),
        rect.width,
        rect.height
      );
      schedule();
    };
    const onDown = (e: PointerEvent) => {
      // Left/primary only — a right-click's pointerup is swallowed by the
      // context menu, which would leave the view stuck in pan mode.
      if (e.button !== 0 || !e.isPrimary) return;
      canvas.setPointerCapture(e.pointerId);
      dragRef.current = { on: true, moved: false, x: e.offsetX, y: e.offsetY };
    };
    const onCancel = () => {
      dragRef.current.on = false;
    };
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.on) {
        const dx = e.offsetX - d.x;
        const dy = e.offsetY - d.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
        camRef.current.panX += dx;
        camRef.current.panY += dy;
        d.x = e.offsetX;
        d.y = e.offsetY;
        if (hoverRef.current !== -1) {
          hoverRef.current = -1;
          propsRef.current.onHover(null);
        }
        schedule();
        return;
      }
      const hit = hitTest(e.offsetX, e.offsetY);
      if (hit !== hoverRef.current) {
        hoverRef.current = hit;
        if (hit >= 0) {
          const a = propsRef.current.atoms[hit];
          propsRef.current.onHover({
            x: e.offsetX,
            y: e.offsetY,
            title: a.title,
            meta: `${a.type} · ${a.scope} · ${ageLabel(a.created_at)}`,
            swatch: propsRef.current.colorOf(a),
          });
        } else {
          propsRef.current.onHover(null);
        }
        schedule();
      } else if (hit >= 0) {
        const a = propsRef.current.atoms[hit];
        propsRef.current.onHover({
          x: e.offsetX,
          y: e.offsetY,
          title: a.title,
          meta: `${a.type} · ${a.scope} · ${ageLabel(a.created_at)}`,
          swatch: propsRef.current.colorOf(a),
        });
      }
    };
    const onUp = (e: PointerEvent) => {
      const d = dragRef.current;
      if (d.on && !d.moved) {
        const hit = hitTest(e.offsetX, e.offsetY);
        propsRef.current.onSelect(hit >= 0 ? propsRef.current.atoms[hit] : null);
        schedule();
      }
      dragRef.current.on = false;
    };
    const onLeave = () => {
      dragRef.current.on = false;
      if (hoverRef.current !== -1) {
        hoverRef.current = -1;
        propsRef.current.onHover(null);
        schedule();
      }
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("pointercancel", onCancel);
    canvas.addEventListener("lostpointercapture", onCancel);
    const ro = new ResizeObserver(schedule);
    ro.observe(canvas);
    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("pointercancel", onCancel);
      canvas.removeEventListener("lostpointercapture", onCancel);
      ro.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0; // reset the guard or StrictMode remount deadlocks schedule()
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas ref={canvasRef} style={{ cursor: "crosshair" }} aria-label="Semantic map of brain atoms" role="img" />;
}
